/**
 * @module scroll-effects
 * Handles scroll-triggered behaviours using IntersectionObserver.
 *
 * ─── JSON API (preferred) ────────────────────────────────────────────────────
 *
 *   data-scroll-trigger='[
 *     {
 *       "call":      "fnName",       required — name registered via registerScrollCall()
 *       "start":     "top bottom",   anchor where enter fires: "element-edge viewport-position"
 *                                    element-edge: top | center | bottom (default: top)
 *                                    viewport-position: top | center | bottom | 0–100 (default: bottom)
 *       "repeat":    false,          if true, also fires 'exit' when element leaves (default: false)
 *       "params":    {},             optional object passed to the callback as third arg
 *       "threshold": 0              0–1 fraction of element that must be visible to trigger.
 *                                    When > 0, "start" is ignored — firing is visibility-based.
 *     }
 *   ]'
 *
 *   Common start values:
 *     "top bottom"   enter fires when element top enters the viewport (default)
 *     "top 0"        enter fires when element top hits the top of the viewport
 *     "top 50"       enter fires when element top reaches the midpoint
 *     "top 90"       enter fires when element is 10% from the bottom (≈ default layoutInView)
 *
 * ─── Legacy API (still supported) ───────────────────────────────────────────
 *
 *   data-scroll-call="fnName"       function to call on enter (space-separated for multiple)
 *   data-scroll-repeat              if present, also fires exit
 *   data-scroll-offset="A%,B%"     maps to rootMargin (first=bottom shrink, second=top shrink)
 *
 * ─── Debug ───────────────────────────────────────────────────────────────────
 *
 *   Add data-scroll-debug to <html> to draw trigger-line overlays and log dispatches.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { parseAnchor } from './scroll-utils.js';

/* ─── Observer store ─────────────────────────────────────────────────────── */

// Key: `${rootMargin}|${threshold}` → { observer, elMap: Map<el, cfg[]> }
const observerGroups   = new Map();
const observedElements = new Set();

/* ─── Debug ──────────────────────────────────────────────────────────────── */

const _isDebug = () => document.documentElement.hasAttribute('data-scroll-debug');

function _updateTriggerOverlay() {
    if (!_isDebug()) return;

    let overlay = document.getElementById('_scroll-trigger-debug');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = '_scroll-trigger-debug';
        overlay.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:99998;';
        document.body.appendChild(overlay);
    }

    overlay.innerHTML = '';
    const COLORS = ['#ff6b6b', '#4ecdc4', '#f7d794', '#a29bfe', '#fd79a8'];
    let ci = 0;

    observerGroups.forEach(({ elMap }, key) => {
        const [margin, threshold] = key.split('|');
        // Parse the viewport-position percentage from the rootMargin string
        const match = margin.match(/^-([\d.]+)%/);
        const vpPct = match ? parseFloat(match[1]) : 0;
        const color = COLORS[ci++ % COLORS.length];

        const calls = [...new Set([...elMap.values()].flat().flatMap(c => c.calls))].join(', ');
        const label = threshold > 0 ? `${threshold * 100}% visible — ${calls}` : `${vpPct}% — ${calls}`;

        const line = document.createElement('div');
        line.style.cssText = `position:absolute;left:0;right:0;top:${vpPct}%;height:1px;background:${color};`;

        const tag = document.createElement('div');
        tag.style.cssText = `position:absolute;left:8px;top:-1px;font:10px/1 monospace;color:${color};background:rgba(0,0,0,.8);padding:2px 6px;white-space:nowrap;`;
        tag.textContent = label;

        line.appendChild(tag);
        overlay.appendChild(line);
    });
}

/* ─── Callback registry ──────────────────────────────────────────────────── */

const scrollCallFunctions = {
    sectionBgColour,
    layoutInView,
    staggerInView,
    staggeredItemInView,
};

/**
 * Register a custom scroll-effects callback so it can be referenced by name
 * in data-scroll-trigger JSON without editing this file.
 *
 * @param {string}   name  Key used in the "call" field of data-scroll-trigger
 * @param {function} fn    (way: 'enter'|'exit', el: HTMLElement, params?: object) => void
 */
export function registerScrollCall(name, fn) {
    scrollCallFunctions[name] = fn;
}

/* ─── Built-in callbacks ─────────────────────────────────────────────────── */

/**
 * Sets data-bgcolor on <body> to match the section's data-style-set.
 */
function sectionBgColour(way, el) {
    if (way === 'enter') {
        const styleSet = el.getAttribute('data-style-set');
        if (styleSet) document.body.setAttribute('data-bgcolor', styleSet);
    }
}

/**
 * Toggles is-inview on the element.
 */
function layoutInView(way, el) {
    if (way === 'enter') {
        el.classList.add('is-inview');
    } else if (way === 'exit') {
        el.classList.remove('is-inview');
    }
}

/**
 * Staggers is-inview across child items.
 * data-stagger-selector=".child"  optional child selector (defaults to direct children)
 * data-stagger-delay="0.1"        per-item delay in seconds (default 0.1)
 */
function staggerInView(way, el) {
    const selector  = el.getAttribute('data-stagger-selector');
    const items     = selector ? el.querySelectorAll(selector) : el.children;
    const delayStep = parseFloat(el.getAttribute('data-stagger-delay')) || 0.1;

    if (way === 'enter') {
        Array.from(items)
            .filter(item => !item.classList.contains('is-inview'))
            .forEach((item, index) => {
                item.style.setProperty('--delay', `${index * delayStep}s`);
                item.style.transitionDelay = `${index * delayStep}s`;
                item.classList.add('is-inview');
            });
        el.classList.add('is-inview');
    } else if (way === 'exit') {
        Array.from(items).forEach(item => {
            item.style.removeProperty('--delay');
            item.style.transitionDelay = '';
            item.classList.remove('is-inview');
        });
        el.classList.remove('is-inview');
    }
}

/**
 * Adds element to a debounced stagger queue — preferred over staggerInView for ALM.
 * Place on individual items rather than the wrapper.
 */
let staggerQueue = [];
let staggerTimer = null;

function processStaggerQueue() {
    if (!staggerQueue.length) return;

    staggerQueue.sort((a, b) =>
        (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1
    );

    const delayStep = parseFloat(staggerQueue[0].getAttribute('data-stagger-delay')) * 1000 || 100;

    staggerQueue.forEach((el, index) => {
        setTimeout(() => el.classList.add('is-inview'), index * delayStep);
    });

    staggerQueue = [];
    staggerTimer = null;
}

function staggeredItemInView(way, el) {
    if (way === 'enter') {
        if (!el.classList.contains('is-inview')) {
            staggerQueue.push(el);
            if (!staggerTimer) staggerTimer = setTimeout(processStaggerQueue, 50);
        }
    } else if (way === 'exit') {
        el.classList.remove('is-inview');
    }
}

/* ─── Anchor → rootMargin ────────────────────────────────────────────────── */

/**
 * Convert a start anchor string to an IntersectionObserver rootMargin.
 * Creates a zero-height trigger line at the viewport-position percentage.
 *
 * "top bottom"  → 0px  (fires when element enters viewport bottom)
 * "top 0"       → top  (fires when element top hits viewport top)
 * "top 90"      → 10%  from bottom
 *
 * @param {string} start  e.g. "top bottom", "top 0"
 * @returns {string}      rootMargin e.g. "-90% 0px -10% 0px"
 */
function startToRootMargin(start) {
    const { vpFrac } = parseAnchor(start || 'top bottom');
    const fromTop    = (vpFrac * 100).toFixed(2);
    const fromBottom = ((1 - vpFrac) * 100).toFixed(2);

    return `-${fromTop}% 0px -${fromBottom}% 0px`;
}

/**
 * Legacy offset string → rootMargin.
 * Input "A%,B%" → top shrink = B%, bottom shrink = A%.
 * @param {string|null} offsetAttr
 * @returns {string}
 */
function getRootMargin(offsetAttr) {
    const defaults = ['10%', '90%'];
    const parts    = offsetAttr ? offsetAttr.split(',') : defaults;

    const format = (val) => {
        let v = (val?.trim()) || '0';
        if (!isNaN(v)) v += 'px';
        return (v !== '0' && !v.startsWith('-')) ? `-${v}` : v;
    };

    const bottom = format(parts[0]);
    const top    = format(parts[1] || defaults[1]);

    return `${top} 0px ${bottom} 0px`;
}

/* ─── Dispatch ───────────────────────────────────────────────────────────── */

function dispatch(way, el, calls, params) {
    calls.forEach(name => {
        const fn = scrollCallFunctions[name];
        if (fn) {
            fn(way, el, params);
            if (_isDebug()) console.log(`[scroll-effects] ${way}: ${name}`, el);
        } else {
            console.warn(`[scroll-effects] Unknown scroll call: "${name}". Register it via registerScrollCall().`);
        }
    });
}

/* ─── Observer factory ───────────────────────────────────────────────────── */

/**
 * Get or create an IntersectionObserver for the given rootMargin + threshold pair.
 * Observers are reused across init and refresh calls.
 *
 * @param {string} rootMargin
 * @param {number} threshold   0–1
 * @returns {{ observer: IntersectionObserver, elMap: Map }}
 */
function _getOrCreateGroup(rootMargin, threshold) {
    const key = `${rootMargin}|${threshold}`;
    if (observerGroups.has(key)) return observerGroups.get(key);

    const elMap = new Map();

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const el   = entry.target;
            const cfgs = elMap.get(el);
            if (!cfgs) return;

            const isPastTrigger = entry.rootBounds &&
                entry.boundingClientRect.top < entry.rootBounds.top;

            const shouldEnter = entry.isIntersecting || isPastTrigger;

            cfgs.forEach(cfg => {
                if (shouldEnter && !cfg._entered) {
                    // Element crossed into or past the trigger — fire enter once
                    cfg._entered = true;
                    dispatch('enter', el, cfg.calls, cfg.params);
                    if (!cfg.repeat) observer.unobserve(el);

                } else if (!shouldEnter && cfg._entered) {
                    // Element returned below the trigger — reset state, fire exit if repeat
                    cfg._entered = false;
                    if (cfg.repeat) dispatch('exit', el, cfg.calls, cfg.params);
                }
                // When shouldEnter && cfg._entered: element moved further past trigger — no double-fire
            });
        });
    }, { root: null, rootMargin, threshold });

    const group = { observer, elMap };
    observerGroups.set(key, group);
    return group;
}

/* ─── Config builder ─────────────────────────────────────────────────────── */

/**
 * Parse DOM into a flat config array. When skipObserved is true, elements
 * already in observedElements are skipped (used by refreshScrollEffects).
 *
 * @param {Element|Document} scope
 * @param {boolean}          skipObserved
 * @returns {Array}
 */
function _buildConfigs(scope = document, skipObserved = false) {
    const allConfigs = [];

    // JSON API: data-scroll-trigger
    scope.querySelectorAll('[data-scroll-trigger]').forEach(el => {
        if (skipObserved && observedElements.has(el)) return;
        try {
            JSON.parse(el.getAttribute('data-scroll-trigger')).forEach(cfg => {
                const threshold = cfg.threshold ?? 0;
                allConfigs.push({
                    el,
                    calls:      [cfg.call].flat().filter(Boolean),
                    repeat:     cfg.repeat ?? false,
                    params:     cfg.params ?? undefined,
                    // threshold > 0 uses full-viewport intersection; start is ignored
                    rootMargin: threshold > 0 ? '0px' : startToRootMargin(cfg.start ?? 'top bottom'),
                    threshold,
                    _entered:   false,
                });
            });
        } catch {
            console.warn('[scroll-effects] Invalid JSON in data-scroll-trigger on', el);
        }
    });

    // Legacy API: data-scroll-call (skipped if element also has data-scroll-trigger)
    scope.querySelectorAll('[data-scroll-call]:not([data-scroll-trigger])').forEach(el => {
        if (skipObserved && observedElements.has(el)) return;
        allConfigs.push({
            el,
            calls:      el.getAttribute('data-scroll-call').split(/\s+/).filter(Boolean),
            repeat:     el.hasAttribute('data-scroll-repeat'),
            params:     undefined,
            rootMargin: getRootMargin(el.getAttribute('data-scroll-offset')),
            threshold:  0,
            _entered:   false,
        });
    });

    return allConfigs;
}

/* ─── Wire configs to observers ──────────────────────────────────────────── */

function _observeConfigs(configs) {
    // Group by rootMargin + threshold so we reuse/share observers
    const byKey = new Map();
    configs.forEach(cfg => {
        const key = `${cfg.rootMargin}|${cfg.threshold}`;
        if (!byKey.has(key)) byKey.set(key, { rootMargin: cfg.rootMargin, threshold: cfg.threshold, cfgs: [] });
        byKey.get(key).cfgs.push(cfg);
    });

    byKey.forEach(({ rootMargin, threshold, cfgs }) => {
        const { observer, elMap } = _getOrCreateGroup(rootMargin, threshold);
        cfgs.forEach(cfg => {
            if (!elMap.has(cfg.el)) elMap.set(cfg.el, []);
            elMap.get(cfg.el).push(cfg);
            observer.observe(cfg.el);
            observedElements.add(cfg.el);
        });
    });
}

/* ─── Init / destroy / refresh ───────────────────────────────────────────── */

/**
 * Initialises scroll-triggered effects for all data-scroll-trigger and
 * legacy data-scroll-call elements on the current page.
 */
export function initializeScrollEffects(container = document) {
    destroyScrollEffects();
    _observeConfigs(_buildConfigs(container));
    _updateTriggerOverlay();
}

/**
 * Observe newly injected elements without tearing down existing observers.
 * Call after ALM or any dynamic DOM insertion.
 *
 * @param {Element|Document} [container=document]  Scope to search within.
 */
export function refreshScrollEffects(container = document) {
    _observeConfigs(_buildConfigs(container, true));
    _updateTriggerOverlay();
}

/**
 * Disconnects all active observers and clears pending stagger state.
 */
export function destroyScrollEffects() {
    observerGroups.forEach(({ observer }) => observer.disconnect());
    observerGroups.clear();
    observedElements.clear();

    // Flush stagger timers so they don't carry over across Barba page transitions
    if (staggerTimer) { clearTimeout(staggerTimer); staggerTimer = null; }
    staggerQueue = [];

    document.getElementById('_scroll-trigger-debug')?.remove();
}